import { IconButton, Tooltip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import DescriptionIcon from "@mui/icons-material/Description";
import CollectionsIcon from "@mui/icons-material/Collections";
import ArticleIcon from "@mui/icons-material/Article";
import { Controller } from "react-hook-form";
import FileInput from "@/components/FormControl/FileInput/FileInput";

interface ChatInputProps {
  input: string;
  setInput: (value: string) => void;
  sendText: () => void;
  attachOpen: boolean;
  setAttachOpen: (value: boolean) => void;
  control:any
  onFileSelect?: (file: File) => void;
  onTemplateClick?: () => void;
}

const ChatInput = ({ input, setInput, sendText, attachOpen, setAttachOpen, control, onFileSelect, onTemplateClick }: ChatInputProps) => {
  
  return (
    <div className="border-t border-gray-100 bg-white px-4 py-3 md:px-6 md:py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)] relative z-10 w-full rounded-b-xl">
      {attachOpen && (
        <div className="absolute bottom-[100%] left-6 mb-2 w-56 rounded-2xl border border-gray-100 bg-white py-2 shadow-xl ring-1 ring-black/5 transform transition-all">
          <button className="flex w-full items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-700 hover:bg-slate-50 transition-colors"
            onClick={() => {
              const input = document.querySelector(
                "#document-input input[type='file']"
              ) as HTMLInputElement;

              input?.click();
            }}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50">
              <DescriptionIcon sx={{ fontSize: 20, color: "#2563eb" }} />
            </div>
            Document
          </button>
          <div>          
            <button className="flex w-full items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-700 hover:bg-slate-50 transition-colors"
              onClick={() => {
                const input = document.querySelector(
                  "#photo-video-input input[type='file']"
                ) as HTMLInputElement;

                input?.click();
              }}>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50">
              <CollectionsIcon sx={{ fontSize: 20, color: "#10b981" }} />
            </div>
            Photos & videos
          </button>
        
            <Controller
              name="Image&video"
              control={control}
              render={({ field }) => (
                <div id="photo-video-input">
                  <FileInput
                    field={field}
                    accept="image/*,video/*"
                    maxSize={5 * 1024 * 1024}
                    hiddenTextField={true}
                    onFileChange={(file) => {
                      if (file && onFileSelect) {
                        onFileSelect(file);
                        setAttachOpen(false);
                      }
                    }}
                  />
                </div>
              )}
            />

            <Controller
              name="document"
              control={control}
              render={({ field }) => (
                <div id="document-input">
                  <FileInput
                    field={field}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    maxSize={5 * 1024 * 1024}
                    hiddenTextField={true}
                    onFileChange={(file) => {
                      if (file && onFileSelect) {
                        onFileSelect(file);
                        setAttachOpen(false);
                      }
                    }}
                  />
                </div>
              )}
            />

         </div>

        </div>
      )}
           
          <div className="flex items-center gap-2 md:gap-3">
              <div className="flex items-center">
                  {/* Attach Button */}
                  <Tooltip title="Attach">
                      <IconButton
                          onClick={() => setAttachOpen(!attachOpen)}
                          sx={{
                              width: 42,
                              height: 42,
                              color: attachOpen ? "#2563eb" : "#64748b",
                              backgroundColor: attachOpen ? "#eff6ff" : "transparent",
                              transition: "all 0.2s",
                              "&:hover": {
                                  backgroundColor: "#f1f5f9",
                                  color: "#334155"
                              },
                          }}
                      >
                          <AddIcon sx={{ fontSize: 24, transform: attachOpen ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s' }} />
                      </IconButton>
                  </Tooltip>

                  {/* Template Button */}
                  <Tooltip title="Template">
                      <IconButton
                          onClick={onTemplateClick}
                          sx={{
                              width: 42,
                              height: 42,
                              color: "#64748b",
                              "&:hover": {
                                  backgroundColor: "#f1f5f9",
                                  color: "#334155"
                              },
                          }}
                      >
                        <ArticleIcon sx={{ fontSize: 22 }} />            
                      </IconButton>
                  </Tooltip>
              </div>

        <div className="flex-1 rounded-full border border-gray-200 bg-gray-50 px-5 py-3 focus-within:ring-2 focus-within:ring-blue-100 focus-within:bg-white focus-within:border-blue-300 transition-all shadow-inner-sm">
          <input
            className="w-full bg-transparent text-[15px] text-gray-800 outline-none placeholder:text-gray-400"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                sendText();
              }
            }}
          />
        </div>

        <Tooltip title="Send">
          <span>
            <IconButton
              onClick={sendText}
              disabled={!input.trim()}
              sx={{
                width: 46,
                height: 46,
                backgroundColor: input.trim() ? "#2563eb" : "#f1f5f9",
                color: input.trim() ? "#ffffff" : "#cbd5e1",
                boxShadow: input.trim() ? "0 4px 6px -1px rgba(37, 99, 235, 0.2)" : "none",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: input.trim() ? "#1d4ed8" : "#f1f5f9",
                  transform: input.trim() ? "translateY(-1px)" : "none"
                },
                "&:active": {
                  transform: input.trim() ? "translateY(0)" : "none"
                }
              }}
            >
              <SendIcon sx={{ fontSize: 20, ml: input.trim() ? 0.5 : 0 }} />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default ChatInput;
